/**
 * 쓰는 곳이 있는 편성 삭제 — 어디서 쓰는지 보여 주고, 그 자리를 어떻게 처리할지 골라 지운다.
 *
 * 처리는 둘이다(2026-10-07 사용자 결정 — 둘 다 고르게): 쓰던 곳을 **다른 것으로 교체**하거나(기본 — 화면이
 * 비지 않는다), **연결을 해제**한다. 어느 쪽이든 무엇이 바뀌는지 누르기 전에 보여 준다. 나가고 있는 긴급
 * 송출이 쓰면 어느 쪽도 내지 않는다 — 긴급은 긴급 패널에서 해제한다(서버도 막는다).
 *
 * **위에서 아래로 한 번만 읽히게 둔다**(2026-10-07 사용자 지적 — 난잡하다). 사용처 → 처리 방법 → 적용
 * 결과 → 삭제 순서이고, 사용처는 종류마다 상자를 따로 치지 않고 한 표에 줄로 적는다. 종류마다 붙어 있던
 * "거기서 할 일" 문장은 뺐다 — 이름이 링크이고, 처리는 이 창에서 고른다.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Modal } from './ui.js';
import { describeError } from '../hooks.js';
import { josa } from '../josa.js';
import {
  DELETE_NOUN,
  forceBlocked,
  groupReferences,
  REFERENCE_KINDS,
  REFERENCE_ORDER,
  releaseConsequences,
  type BlockedBy,
  type DeleteSubject,
} from '../references.js';
import type { DeleteRelease } from '../api/client.js';

export interface ForceDelete {
  /** 대신 쓸 수 있는 것 — 지우려는 것은 빼고 준다. */
  candidates: ReadonlyArray<{ id: string; name: string }>;
  /** 그래도 지운다. 끝나면 부르는 쪽이 목록을 다시 읽는다. */
  run: (release: DeleteRelease) => Promise<void>;
}

export function BlockedDeleteModal({
  subject,
  name,
  blocked,
  force,
  onClose,
}: {
  subject: DeleteSubject;
  /** 지우려던 것의 이름 — 「」 없이. */
  name: string;
  blocked: BlockedBy;
  force?: ForceDelete;
  onClose: () => void;
}): ReactNode {
  const noun = DELETE_NOUN[subject];
  const groups = groupReferences(blocked);
  const total = REFERENCE_ORDER.reduce((sum, kind) => sum + blocked.counts[kind], 0);
  const [mode, setMode] = useState<'replace' | 'detach'>('replace');
  const [replacementId, setReplacementId] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const replacement = force?.candidates.find((candidate) => candidate.id === replacementId) ?? null;
  const consequences = force ? releaseConsequences(subject, mode, blocked.counts, replacement?.name ?? null) : [];
  const canForce = Boolean(force) && !forceBlocked(blocked);

  const submit = async (): Promise<void> => {
    if (!force) return;
    const release: DeleteRelease | null =
      mode === 'detach' ? { mode: 'detach' } : replacement ? { mode: 'replace', replacementId: replacement.id } : null;
    if (!release) return;
    setPending(true);
    setError(null);
    try {
      await force.run(release);
      onClose();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal title={`${noun} 삭제`} onClose={onClose}>
      <div className="del-ref">
        <p className="del-ref__lead">
          <strong>「{name}」</strong>
          {josa(name, '은/는')} {total}곳에서 사용 중입니다.
          {canForce ? ' 사용 중인 곳의 처리 방법을 선택한 뒤 삭제하세요.' : null}
        </p>

        <section aria-labelledby="del-ref-usage">
          <h4 id="del-ref-usage" className="del-ref__label">사용 중인 곳</h4>
          <dl className="del-ref__usage">
            {groups.map(({ kind, items, more }) => {
              const info = REFERENCE_KINDS[kind];
              return (
                <div key={kind} className="del-ref__usage-row">
                  <dt>{info.label}</dt>
                  <dd>
                    {items.map((ref) => (
                      <span key={`${kind}-${ref.id}`} className="del-ref__item">
                        {info.href ? (
                          <Link to={info.href(ref)} onClick={onClose}>
                            {ref.name}
                          </Link>
                        ) : (
                          ref.name
                        )}
                        {ref.note ? <Badge>{ref.note}</Badge> : null}
                      </span>
                    ))}
                    {more > 0 ? (
                      <span className="cell-muted">
                        외 {more}
                        {info.unit}
                      </span>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>

        {force && !canForce ? (
          <p className="note note-error" role="status">
            진행 중인 긴급 송출에서 사용 중이므로 삭제할 수 없습니다. 캠페인 화면의 긴급 패널에서 먼저 해제하세요.
          </p>
        ) : null}

        {force && canForce ? (
          <>
            <section aria-labelledby="del-ref-mode">
              <h4 id="del-ref-mode" className="del-ref__label">처리 방법</h4>
              <div className="del-ref__modes" role="radiogroup" aria-labelledby="del-ref-mode">
                <label className="del-ref__mode">
                  <input type="radio" name="release" checked={mode === 'replace'} onChange={() => setMode('replace')} />
                  <span>
                    <strong>
                      다른 {noun}로 교체 <Badge tone="info">권장</Badge>
                    </strong>
                    <span className="del-ref__mode-desc">선택한 {noun}로 대체합니다.</span>
                  </span>
                </label>
                <label className="del-ref__mode">
                  <input type="radio" name="release" checked={mode === 'detach'} onChange={() => setMode('detach')} />
                  <span>
                    <strong>연결 해제</strong>
                    <span className="del-ref__mode-desc">사용 중인 곳에서 이 {noun}{josa(noun, '을/를')} 제외합니다.</span>
                  </span>
                </label>
              </div>
            </section>

            {mode === 'replace' ? (
              <label className="field del-ref__replacement">
                <span>교체할 {noun}</span>
                <select
                  className="input"
                  value={replacementId}
                  onChange={(event) => setReplacementId(event.target.value)}
                >
                  <option value="">선택하세요</option>
                  {force.candidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {consequences.length > 0 ? (
              <section aria-labelledby="del-ref-result">
                <h4 id="del-ref-result" className="del-ref__label">적용 결과</h4>
                <dl className="del-ref__result">
                  {consequences.map((line) => (
                    <div key={line.target} className="del-ref__result-row">
                      <dt>{line.target}</dt>
                      <dd className={line.danger ? 'del-ref__danger' : undefined}>{line.effect}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            {error ? (
              <p className="note note-error" role="alert">
                {error}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="form-actions del-ref__actions">
        {canForce ? (
          <p className="hint mr-auto">
            삭제한 {noun}
            {josa(noun, '은/는')} 복구할 수 없습니다.
          </p>
        ) : null}
        <button type="button" className="btn" onClick={onClose} disabled={pending}>
          {canForce ? '취소' : '닫기'}
        </button>
        {canForce ? (
          <button
            type="button"
            className={mode === 'detach' ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={pending || (mode === 'replace' && !replacement)}
            onClick={() => void submit()}
          >
            {pending ? '삭제하는 중…' : mode === 'replace' ? '교체 후 삭제' : '해제 후 삭제'}
          </button>
        ) : null}
      </div>
    </Modal>
  );
}
